import { getErrorMessage } from '../../utils/errorMessage';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Check,
  ChevronDown,
  Crown,
  Gem,
  Landmark,
  RefreshCw,
  Sparkles,
  Users,
  Zap,
  CreditCard,
  ShieldCheck,
  X,
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import paymentApi from '../../utils/paymentApi';
import { paymentErrorMessage, paymentResponseError } from './paymentErrors';
import { getCurrentTeam } from './teamApi';
import PaymentChannelReadiness from './PaymentChannelReadiness';
import {
  canRequestPayment,
  DEFAULT_PAYMENT_CHANNELS,
  getPaymentChannels,
  opensCashierWindow,
  paymentMethodHint,
  paymentMethodLabel,
  paymentPrefix,
  UNIONPAY_B2B_CODE,
} from './paymentChannels';
import { useTeamBrand } from '../branding/TeamBrandContext';

const PLAN_TONES = ['plain', 'lavender', 'ink', 'sage'];
const PLAN_ICONS = [Sparkles, Zap, Crown, Users];
const PACKAGE_CATALOG_URL = '/package-plan/list';

function visibleDescription(value) {
  return String(value || '').trim();
}

function normalizePlan(rawPlan, index) {
  const plan = rawPlan && typeof rawPlan === 'object' ? rawPlan : {};
  const tags = Array.isArray(plan.tags)
    ? plan.tags.map(item => String(item || '').trim()).filter(Boolean).slice(0, 3)
    : [];
  return {
    id: plan.id ?? `plan-${index}`,
    name: String(plan.name || ''),
    icon: String(plan.icon || ''),
    description: visibleDescription(plan.description),
    details: Array.isArray(plan.details)
      ? plan.details.map(item => String(item || '').trim()).filter(Boolean)
      : [],
    tags,
    monthlyPrice: plan.monthly_price,
    yearlyPrice: plan.yearly_price,
    monthlyGiftCredits: Number(plan.monthly_gift_credits || 0),
    yearlyGiftCredits: Number(plan.yearly_gift_credits || 0),
    sort: Number(plan.sort || 0),
    entitlements: Array.isArray(plan.entitlements)
      ? plan.entitlements.map(item => ({
        id: item?.entitlement_id ?? item?.name,
        name: String(item?.name || item?.entitlement_name || ''),
        value: String(item?.value || item?.entitlement_value_name || ''),
      })).filter(item => item.name)
      : [],
    tone: PLAN_TONES[index % PLAN_TONES.length],
    Icon: PLAN_ICONS[index % PLAN_ICONS.length],
  };
}

function concurrencyLabel(limit, teamScope, english) {
  const parsed = Number(limit);
  const normalized = Number.isInteger(parsed) && parsed >= 0 ? parsed : 2;
  if (english) {
    if (normalized === 0) return teamScope ? 'Unlimited shared team concurrency' : 'Unlimited personal concurrency';
    return teamScope ? `${normalized} shared team tasks` : `${normalized} personal tasks`;
  }
  if (normalized === 0) return teamScope ? '团队共享不限并发' : '个人不限并发';
  return teamScope ? `团队共享 ${normalized} 个并发任务` : `个人 ${normalized} 个并发任务`;
}

function priceLabel(plan, billingCycle, english) {
  const rawPrice = billingCycle === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice;
  if (rawPrice === null || rawPrice === undefined || rawPrice === '') {
    return '暂不可购买';
  }
  const price = Number(rawPrice);
  if (!Number.isFinite(price) || price <= 0) return '暂不可购买';
  return `¥${price.toFixed(2)}`;
}

function giftCreditsLabel(plan, billingCycle, english) {
  const rawCredits = billingCycle === 'monthly' ? plan.monthlyGiftCredits : plan.yearlyGiftCredits;
  const credits = Number.isInteger(rawCredits) && rawCredits >= 0 ? rawCredits : 0;
  return english
    ? `${credits.toLocaleString('en-US')} credits every month`
    : `每月赠送 ${credits.toLocaleString('zh-CN')} 点`;
}

function billingExpirationNotice(billingCycle, english) {
  if (billingCycle === 'yearly') {
    return english
      ? 'Expires at 00:00 on the same date next year. Credits are issued in 12 monthly grants; the first 11 expire at the next grant, and the last expires with the subscription.'
      : '年付于次年同日 00:00 到期；赠点分 12 期逐月发放，前 11 期在下期发放时过期，第 12 期随订阅到期失效。';
  }
  return english
    ? 'The subscription and bonus credits expire at 00:00 on the same date next month. Any unused credits are then cleared automatically.'
    : '月付订阅与赠点将于下个月同日 00:00 到期，未使用点数届时自动清除。';
}

export function SubscriptionCheckoutDialog({ plan, billingCycle, team, english, onClose }) {
  const [method, setMethod] = useState('wechat');
  const [paymentChannels, setPaymentChannels] = useState(DEFAULT_PAYMENT_CHANNELS);
  const [paymentChannelStatus, setPaymentChannelStatus] = useState('loading');
  const [step, setStep] = useState('confirm');
  const [submitting, setSubmitting] = useState(false);
  const [payNo, setPayNo] = useState('');
  const [codeUrl, setCodeUrl] = useState('');
  const [wechatExpiresAt, setWechatExpiresAt] = useState(null);
  const [error, setError] = useState('');
  const [statusError, setStatusError] = useState('');
  const [closingPayment, setClosingPayment] = useState(false);

  useEffect(() => {
    if (step !== 'success') return;
    setClosingPayment(false);
    window.dispatchEvent(new Event('credits-updated'));
  }, [step]);
  const pollRef = useRef(null);
  const channelRequestRef = useRef(0);
  const billingType = billingCycle === 'yearly' ? 2 : 1;
  const price = Number(billingCycle === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice);
  const credits = Number(billingCycle === 'yearly' ? plan.yearlyGiftCredits : plan.monthlyGiftCredits);
  const visiblePaymentChannels = paymentChannels.filter(channel => channel.visible);
  const selectedPaymentChannel = paymentChannels.find(channel => channel.code === method);

  const loadPaymentChannels = useCallback(async () => {
    const requestId = channelRequestRef.current + 1;
    channelRequestRef.current = requestId;
    setPaymentChannelStatus('loading');
    try {
      const channels = await getPaymentChannels();
      if (channelRequestRef.current !== requestId) return;
      setPaymentChannels(channels);
      setMethod(currentCode => (
        channels.find(channel => channel.code === currentCode && channel.visible)
          ? currentCode
          : channels.find(channel => channel.visible && channel.enabled)?.code
            || channels.find(channel => channel.visible)?.code
            || ''
      ));
      setPaymentChannelStatus('ready');
    } catch {
      if (channelRequestRef.current !== requestId) return;
      setPaymentChannels(DEFAULT_PAYMENT_CHANNELS);
      setPaymentChannelStatus('error');
    }
  }, []);

  useEffect(() => {
    loadPaymentChannels();
    return () => { channelRequestRef.current += 1; };
  }, [loadPaymentChannels]);

  const cancelPayment = useCallback(async () => {
    if (step !== 'paying' || !payNo) {
      onClose();
      return;
    }
    if (method !== 'wechat') {
      onClose();
      return;
    }
    setClosingPayment(true);
    setError('');
    try {
      const response = await paymentApi.post('/wechat-pay/cancel', { pay_no: payNo });
      if (Number(response?.data?.code) !== 200) {
        throw paymentResponseError(response?.data);
      }
      if (response?.data?.data?.settled) {
        setStep('success');
        return;
      }
      onClose();
    } catch (requestError) {
      setError(paymentErrorMessage(requestError, english, 'cancel'));
      setClosingPayment(false);
    }
  }, [english, method, onClose, payNo, step]);

  const closeCheckout = useCallback(() => {
    if (closingPayment) return;
    cancelPayment();
  }, [cancelPayment, closingPayment]);

  useEffect(() => {
    const oldOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const escape = event => event.key === 'Escape' && closeCheckout();
    window.addEventListener('keydown', escape);
    return () => {
      document.body.style.overflow = oldOverflow;
      window.removeEventListener('keydown', escape);
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
  }, [closeCheckout]);

  useEffect(() => {
    if (step !== 'paying' || !payNo) return undefined;
    let stopped = false;
    pollRef.current = window.setInterval(async () => {
      try {
        const response = await paymentApi.get(`${paymentPrefix(method)}/status`, { params: { pay_no: payNo } });
        if (Number(response?.data?.code) !== 200) throw paymentResponseError(response?.data);
        if (stopped) return;
        setStatusError('');
        if (response.data?.data?.status === 'success') {
          window.clearInterval(pollRef.current);
          setStep('success');
        }
      } catch (requestError) {
        if (!stopped) setStatusError(paymentErrorMessage(requestError, english, 'query'));
      }
    }, 3000);
    return () => { stopped = true; window.clearInterval(pollRef.current); };
  }, [english, method, payNo, step]);

  useEffect(() => {
    if (step !== 'paying' || method !== 'wechat' || !codeUrl || !wechatExpiresAt) {
      return undefined;
    }
    const expiresAtMs = new Date(wechatExpiresAt).getTime();
    if (!Number.isFinite(expiresAtMs)) return undefined;
    const delay = expiresAtMs - Date.now();
    if (delay <= 0) {
      setCodeUrl('');
      setPayNo('');
      setWechatExpiresAt(null);
      setStep('confirm');
      setError('微信二维码已过期，请重新生成支付二维码');
      return undefined;
    }
    const timer = window.setTimeout(() => {
      setCodeUrl('');
      setPayNo('');
      setWechatExpiresAt(null);
      setStep('confirm');
      setError('微信二维码已过期，请重新生成支付二维码');
    }, delay);
    return () => window.clearTimeout(timer);
  }, [codeUrl, english, method, step, wechatExpiresAt]);

  const pay = async () => {
    if (!method || !canRequestPayment(selectedPaymentChannel) || !paymentPrefix(method)) {
      setError(getErrorMessage(paymentMethodHint(selectedPaymentChannel, english)));
      return;
    }
    setSubmitting(true);
    setError('');
    setStatusError('');
    const payWindow = opensCashierWindow(method) ? window.open('', '_blank') : null;
    if (opensCashierWindow(method) && !payWindow) {
      setSubmitting(false);
      setError(getErrorMessage(english ? 'Please allow pop-ups and try again.' : '支付窗口被浏览器拦截，请允许弹窗后重试', '支付窗口被浏览器拦截，请允许弹窗后重试'));
      return;
    }
    try {
      const response = await paymentApi.post(`${paymentPrefix(method)}/pay`, {
        order_type: team ? 'team_subscription' : 'user_subscription',
        plan_id: plan.id,
        billing_type: billingType,
        ...(method === 'alipay' ? { scene: 'page' } : {}),
      });
      const body = response?.data;
      if (Number(body?.code) !== 200) {
        throw paymentResponseError(body);
      }
      if (Number(body.data?.amount).toFixed(2) !== price.toFixed(2)) {
        throw new Error(english ? 'The plan price changed. Please confirm again.' : '套餐价格已变化，请刷新后重新确认');
      }
      if (!body.data?.pay_no) throw new Error(english ? 'The payment number is missing.' : '支付单号缺失，请稍后重试');
      if (body.data?.status === 'success') {
        payWindow?.close();
        setPayNo(body.data.pay_no);
        setStep('success');
        return;
      }
      if (method === 'wechat') {
        if (!body.data?.code_url) throw new Error('微信支付二维码获取失败');
        setCodeUrl(body.data.code_url);
        setWechatExpiresAt(body.data?.expires_at || null);
      } else {
        if (!body.data?.form) throw new Error(english ? 'Missing cashier form.' : '支付收银台信息缺失');
        payWindow.document.write(body.data.form);
        payWindow.document.close();
      }
      setPayNo(body.data.pay_no);
      setStep('paying');
    } catch (requestError) {
      payWindow?.close();
      setError(paymentErrorMessage(requestError, english));
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal((
    <div className="billing-checkout-backdrop" role="presentation" onMouseDown={event => event.target === event.currentTarget && closeCheckout()}>
      <section className="billing-checkout" role="dialog" aria-modal="true" aria-labelledby="subscription-checkout-title">
        <header><div><span className="account-eyebrow">SUBSCRIPTION CHECKOUT</span><h2 id="subscription-checkout-title">{english ? 'Confirm subscription' : '确认订阅'}</h2></div><button type="button" onClick={closeCheckout} disabled={closingPayment} aria-label={english ? 'Close' : '关闭'}><X size={17} /></button></header>
        <div className="billing-checkout__body">
          <section className="billing-order-summary">
            <div><span>{english ? 'Plan' : '套餐'}</span><strong>{plan.name}</strong></div>
            <div><span>{english ? 'Billing cycle' : '付费周期'}</span><strong>{billingCycle === 'yearly' ? (english ? 'Yearly' : '年付') : (english ? 'Monthly' : '月付')}</strong></div>
            <div><span>{english ? 'Destination' : '到账账户'}</span><strong>{team ? (english ? 'Current team' : '当前团队') : (english ? 'Personal account' : '个人账户')}</strong></div>
            <div><span>{english ? 'Monthly grant' : '每月赠点'}</span><strong>{credits.toLocaleString()}</strong></div>
            {billingCycle === 'yearly' && <div><span>{english ? 'Grant schedule' : '发放期数'}</span><strong>{english ? '12 monthly grants' : '逐月发放 12 期'}</strong></div>}
            <div className="billing-order-summary__total"><span>{english ? 'Full amount due' : '全额应付'}</span><strong>¥{price.toFixed(2)}</strong></div>
          </section>
          <p className="billing-plan-card__expiry">{billingExpirationNotice(billingCycle, english)}</p>
          <section className="billing-checkout-section"><div className="billing-checkout-section__heading"><h3>{english ? 'Payment method' : '支付方式'}</h3><span role="status" aria-live="polite">{paymentMethodHint(selectedPaymentChannel, english)}</span></div><div className="billing-payment-methods">
            {visiblePaymentChannels.map(channel => (
              <button type="button" key={channel.code} className={`${method === channel.code ? 'is-selected' : ''}${!channel.enabled && channel.code !== 'alipay' ? ' is-unavailable' : ''}`.trim()} aria-pressed={method === channel.code} disabled={step !== 'confirm' || submitting} title={paymentMethodHint(channel, english)} onClick={() => { setError(''); setMethod(channel.code); }}>
                {channel.code === 'wechat'
                  ? <span className="billing-payment-methods__wechat">{english ? 'W' : '微'}</span>
                  : channel.code === UNIONPAY_B2B_CODE
                    ? <span className="billing-payment-methods__bank"><Landmark size={15} /></span>
                    : <CreditCard size={17} />}
                <span><strong>{paymentMethodLabel(channel.code, english)}</strong><small>{paymentMethodHint(channel, english)}</small></span>
                {channel.code === UNIONPAY_B2B_CODE && <em className={`billing-payment-methods__badge ${channel.enabled ? 'is-ready' : ''}`}>{channel.enabled ? (english ? 'Test ready' : '可测试') : ('暂不可用')}</em>}
                {channel.code !== UNIONPAY_B2B_CODE && channel.code !== 'alipay' && !channel.enabled && <em className="billing-payment-methods__badge">{'暂不可用'}</em>}
              </button>
            ))}
          </div><PaymentChannelReadiness channel={selectedPaymentChannel} status={paymentChannelStatus} onRetry={loadPaymentChannels} english={english} /></section>
          {step === 'paying' && method === 'wechat' && codeUrl && <section className="billing-wechat-qr" aria-live="polite"><span><QRCodeSVG value={codeUrl} size={154} level="M" /></span><div><h3>{english ? 'Scan to subscribe' : '扫码完成订阅'}</h3><p>{english ? 'The first monthly grant arrives after payment is confirmed.' : '支付确认后只发放第 1 期赠点。'}</p>{wechatExpiresAt && <p className="billing-qr-expiry">{english ? 'QR code valid until' : '二维码有效期至'} {new Date(wechatExpiresAt).toLocaleTimeString(english ? 'en-US' : 'zh-CN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</p>}<code>{payNo}</code></div></section>}
          {step === 'paying' && opensCashierWindow(method) && <section className="billing-redirect-payment" aria-live="polite"><span>{method === UNIONPAY_B2B_CODE ? <Landmark size={19} /> : <CreditCard size={19} />}</span><div><h3>{english ? `${paymentMethodLabel(method, true)} cashier opened` : `已打开${paymentMethodLabel(method)}收银台`}</h3><p>{english ? 'Complete payment in the new window, then return here.' : '请在新窗口完成付款，支付结果确认后将自动生效。'}</p><code>{payNo}</code></div></section>}
        </div>
        {step !== 'success' && (error || statusError) && <p className="billing-checkout__error" role="alert" aria-atomic="true">{error || statusError}</p>}
        <footer>
          <div><ShieldCheck size={14} /><span>{english ? 'No proration or credit. The selected plan is charged in full.' : '暂不补差价或抵扣，本次按目标套餐全额支付。'}</span></div>
          {step === 'confirm' ? (
            <button type="button" disabled={submitting || (method === UNIONPAY_B2B_CODE && paymentChannelStatus !== 'ready') || !canRequestPayment(selectedPaymentChannel)} title={!canRequestPayment(selectedPaymentChannel) ? paymentMethodHint(selectedPaymentChannel, english) : undefined} onClick={pay}>{submitting ? (english ? 'Creating order…' : '正在创建订单…') : `${paymentMethodLabel(method, english)} ¥${price.toFixed(2)}`}</button>
          ) : (
            <>
              {/*{step === 'paying' && (*/}
              {/*  <button type="button" className="billing-checkout-cancel" onClick={cancelPayment} disabled={closingPayment}>*/}
              {/*    {closingPayment ? (english ? 'Cancelling…' : '正在取消…') : (english ? 'Cancel payment' : '取消支付')}*/}
              {/*  </button>*/}
              {/*)}*/}
              <button type="button" disabled={step !== 'success'} onClick={onClose}>{step === 'success' ? (english ? 'Subscription active · Done' : '订阅成功，完成') : (english ? 'Waiting for payment…' : '等待支付确认…')}</button>
            </>
          )}
        </footer>
      </section>
    </div>
  ), document.body);
}

export default function SubscriptionPlansPanel({
  user,
  english = false,
  planType = 1,
  teamCatalog = false,
}) {
  const brand = useTeamBrand();
  const [billingCycles, setBillingCycles] = useState({});
  const [activePlanType, setActivePlanType] = useState(planType);
  const [plans, setPlans] = useState([]);
  const [catalogStatus, setCatalogStatus] = useState('loading');
  const [reloadKey, setReloadKey] = useState(0);
  const [account, setAccount] = useState(null);
  const [currentTeam, setCurrentTeam] = useState(null);
  const [selectedCheckout, setSelectedCheckout] = useState(null);
  const hasVisiblePlansRef = useRef(false);
  const catalogPlanType = teamCatalog ? planType : activePlanType;
  const teamScope = catalogPlanType === 2;

  const loadAccountContext = useCallback(async () => {
    try {
      const [accountResponse, teamResponse] = await Promise.all([
        paymentApi.get('/credit-account'),
        teamScope ? getCurrentTeam().catch(() => null) : Promise.resolve(null),
      ]);
      setAccount(Number(accountResponse?.data?.code) === 200 ? accountResponse.data.data : null);
      setCurrentTeam(teamResponse);
    } catch {
      setAccount(null);
      setCurrentTeam(null);
    }
  }, [teamScope]);

  useEffect(() => { loadAccountContext(); }, [loadAccountContext, reloadKey]);

  useEffect(() => {
    window.addEventListener('credits-updated', loadAccountContext);
    return () => window.removeEventListener('credits-updated', loadAccountContext);
  }, [loadAccountContext]);

  useEffect(() => {
    setActivePlanType(planType);
  }, [planType]);

  useEffect(() => {
    setBillingCycles({});
  }, [catalogPlanType]);

  useEffect(() => {
    let active = true;
    setCatalogStatus(hasVisiblePlansRef.current ? 'stale' : 'loading');
    paymentApi.get(PACKAGE_CATALOG_URL, { params: { plan_type: catalogPlanType } })
      .then(response => {
        const body = response?.data;
        if (Number(body?.code) !== 200) {
          throw new Error(body?.message || 'PACKAGE_CATALOG_FAILED');
        }
        const rows = Array.isArray(body?.data?.list) ? body.data.list : [];
        if (active) {
          const normalizedPlans = rows.map(normalizePlan);
          hasVisiblePlansRef.current = normalizedPlans.length > 0;
          setPlans(normalizedPlans);
          setCatalogStatus('ready');
        }
      })
      .catch(() => {
        if (active) {
          hasVisiblePlansRef.current = false;
          setPlans([]);
          setCatalogStatus('error');
        }
      });
    return () => { active = false; };
  }, [catalogPlanType, reloadKey]);

  const matrixRows = useMemo(() => {
    const rows = new Map();
    plans.forEach(plan => {
      plan.entitlements.forEach(entitlement => {
        const key = String(entitlement.id ?? entitlement.name);
        if (!rows.has(key)) {
          rows.set(key, { key, name: entitlement.name, values: {} });
        }
        rows.get(key).values[String(plan.id)] = entitlement.value || '—';
      });
    });
    return Array.from(rows.values());
  }, [plans]);

  const catalogBadge = catalogStatus === 'error'
    ? ('套餐暂不可用')
    : (english ? 'Loading plans' : '正在加载套餐');
  const activeSubscription = account?.subscriptions?.[teamScope ? 'team' : 'personal'] || null;
  const activeConcurrency = account?.concurrency?.[teamScope ? 'team' : 'personal'];
  const activeCatalogPlan = plans.find(plan => Number(plan.id) === Number(activeSubscription?.plan_id));
  const canPayTeam = !teamScope || ['owner', 'admin'].includes(currentTeam?.role);

  const purchaseState = (plan, billingCycle) => {
    const price = Number(billingCycle === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice);
    if (!Number.isFinite(price) || price <= 0) return { disabled: true, label: '暂不可购买', reason: '此付费周期暂无有效价格，请选择其他周期或套餐。' };
    if (teamScope && !canPayTeam) return { hidden: true };
    if (!activeSubscription || !activeCatalogPlan) return { disabled: false, label: english ? 'Subscribe now' : '立即订阅' };
    const targetBilling = billingCycle === 'yearly' ? 2 : 1;
    if (Number(plan.sort) < Number(activeCatalogPlan.sort)) return { disabled: true, label: '有效期内不可降级' };
    if (Number(activeSubscription.billing_type) === 2
      && targetBilling === 1
      && Number(plan.id) !== Number(activeSubscription.plan_id)) {
      return { disabled: true, label: english ? 'Annual plan active' : '年付有效期内不可改其他套餐月付' };
    }
    if (Number(plan.id) === Number(activeSubscription.plan_id)) {
      if (Number(activeSubscription.billing_type) === targetBilling) return { disabled: true, label: english ? 'Current plan' : '当前已生效' };
      if (Number(activeSubscription.billing_type) === 2 && targetBilling === 1) return { disabled: true, label: english ? 'Annual plan active' : '年付有效期内不可改月付' };
      return { disabled: false, label: english ? 'Switch at full annual price' : '按年付全额升级' };
    }
    return { disabled: false, label: english ? 'Upgrade at full price' : '按目标套餐全额升级' };
  };

  return (
    <>
      <section className="account-page-heading" id={teamCatalog ? 'team-plan-catalog' : undefined}>
        <div>
          <span className="account-eyebrow">{teamCatalog ? 'TEAM SUBSCRIPTION' : 'SUBSCRIPTION'}</span>
          <h1>{teamCatalog ? (english ? 'Team Plans' : '团队套餐') : (english ? 'Subscription Plans' : '订阅套餐')}</h1>
          <p>{teamCatalog
            ? (english ? 'Compare team subscription prices, benefits, and bonus credits.' : '查看团队套餐的价格、权益和订阅赠点。')
            : (english ? 'Compare subscription prices, benefits, and bonus credits.' : '查看套餐价格、权益和订阅赠点。')}</p>
        </div>
        {catalogStatus !== 'ready' && catalogStatus !== 'stale' && <span className="account-status account-status--warm" role="status">{catalogBadge}</span>}
      </section>

      <section className="billing-current-plan">
        <div className="billing-current-plan__icon"><Crown size={21} /></div>
        <div>
          <span>{teamScope ? (english ? 'Current team plan' : '当前团队套餐') : (english ? 'Current plan' : '当前套餐')}</span>
          <strong>{activeSubscription?.plan_name || (english ? 'No active subscription' : '暂无有效订阅')}</strong>
          <p>{activeSubscription
            ? `${concurrencyLabel(activeConcurrency?.limit ?? activeSubscription.concurrency_limit, teamScope, english)} · ${english ? 'Granted period' : '已发放第'} ${activeSubscription.granted_periods}/${activeSubscription.grant_periods} ${english ? '' : '期'} · ${english ? 'This period' : '本期'} ${Number(activeSubscription.monthly_gift_credits || 0).toLocaleString(english ? 'en-US' : 'zh-CN')} ${english ? 'credits' : '点'} · ${english ? 'Next grant' : '下次赠点'} ${activeSubscription.next_credit_grant_at ? new Date(activeSubscription.next_credit_grant_at).toLocaleString(english ? 'en-US' : 'zh-CN') : (english ? 'No further grant' : '本周期无后续发放')} · ${english ? 'Expires' : '订阅到期'} ${new Date(activeSubscription.expires_at).toLocaleString(english ? 'en-US' : 'zh-CN')}`
            : `${concurrencyLabel(activeConcurrency?.limit ?? 2, teamScope, english)} · ${english ? 'Choose a plan below. Only one subscription can be active at a time.' : '免费额度；可在下方选择套餐，同一时间只保留一个有效订阅。'}`}</p>
        </div>
        <div className="billing-current-plan__balance">
          <span><Gem size={13} />{teamScope ? (english ? 'Current team balance' : '当前团队点数') : (english ? 'Credit balance' : '当前点数')}</span>
          <strong>{Number(teamScope
            ? (currentTeam?.credits ?? account?.team_wallet?.credits ?? 0)
            : (account?.wallet?.available_credits ?? user?.credits ?? 0)).toLocaleString(english ? 'en-US' : 'zh-CN')}</strong>
        </div>
      </section>

      <div className="billing-plan-toolbar">
        <div>
          <span className="account-eyebrow">CHOOSE A PLAN</span>
          <h2>{teamCatalog
            ? (english ? 'Choose a team plan' : '选择团队套餐')
            : (english ? 'Choose how you create' : '选择适合你的创作方式')}</h2>
        </div>
      </div>

      {!teamCatalog && (
          <div className="billing-membership-tabs" role="tablist" aria-label={english ? 'Subscription type' : '订阅类型'}>
            <button
                type="button"
                role="tab"
                aria-selected={activePlanType === 1}
                className={activePlanType === 1 ? 'is-active' : ''}
                onClick={() => setActivePlanType(1)}
            >
              {english ? 'Personal Subscription' : '个人订阅'}
            </button>
            <button
                type="button"
                role="tab"
                aria-selected={activePlanType === 2}
                className={activePlanType === 2 ? 'is-active' : ''}
                onClick={() => setActivePlanType(2)}
            >
              {english ? 'Team Subscription' : '团队订阅'}
            </button>
          </div>
      )}

      {catalogStatus === 'loading' && plans.length === 0 ? (
        <section className="billing-plan-state" aria-live="polite">
          <RefreshCw size={18} />
          <strong>{english ? 'Loading subscription plans…' : '正在加载订阅套餐…'}</strong>
        </section>
      ) : catalogStatus === 'error' ? (
        <section className="billing-plan-state" role="alert">
          <strong>{'套餐目录加载失败'}</strong>
          <span>{english ? 'Try again later.' : '请稍后重试。'}</span>
          <button type="button" onClick={() => setReloadKey(value => value + 1)}>
            <RefreshCw size={13} />{english ? 'Retry' : '重新加载'}
          </button>
        </section>
      ) : plans.length === 0 ? (
        <section className="billing-plan-state" aria-live="polite">
          <strong>{english ? 'No subscription plans are available.' : '暂无可订阅套餐'}</strong>
        </section>
      ) : (
        <section className="billing-plan-grid">
          {plans.map(plan => {
            const Icon = plan.Icon;
            const billingCycle = billingCycles[String(plan.id)] || 'monthly';
            const setPlanBillingCycle = cycle => setBillingCycles(current => ({
              ...current,
              [String(plan.id)]: cycle,
            }));
            const action = purchaseState(plan, billingCycle);
            return (
              <article
                key={plan.id}
                className={`billing-plan-card billing-plan-card--${plan.tone}${plan.tags.length ? ' is-featured' : ''}`}
              >
                {plan.tags.length > 0 && (
                  <div className="billing-plan-card__tags" aria-label={english ? 'Plan tags' : '套餐标签'}>
                    {plan.tags.map(tag => <span key={tag}>{tag}</span>)}
                  </div>
                )}
                <div className="billing-plan-card__icon">
                  {plan.icon ? <img src={plan.icon} alt="" /> : <Icon size={18} />}
                </div>
                <h3>{plan.name}</h3>
                {plan.description && <p className="billing-plan-card__note">{plan.description}</p>}
                <div
                  className="billing-package-options billing-package-options--two"
                  role="radiogroup"
                  aria-label={english ? `${plan.name} billing cycle` : `${plan.name}付费周期`}
                >
                  <button
                    type="button"
                    role="radio"
                    aria-checked={billingCycle === 'monthly'}
                    className={billingCycle === 'monthly' ? 'is-active' : ''}
                    aria-label={english ? 'Pay Monthly' : '按月付费'}
                    onClick={() => setPlanBillingCycle('monthly')}
                  >
                    <strong>{english ? 'Monthly' : '月付'}</strong>
                    {/*<span>{english ? '1 month' : '1个月'}</span>*/}
                  </button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={billingCycle === 'yearly'}
                    className={billingCycle === 'yearly' ? 'is-active' : ''}
                    aria-label={english ? 'Pay Yearly' : '按年付费'}
                    onClick={() => setPlanBillingCycle('yearly')}
                  >
                    <strong>{english ? 'Yearly' : '年付'}</strong>
                    {/*<span>{english ? '1 year' : '1 年'}</span>*/}
                  </button>
                </div>
                <div className="billing-plan-card__price">
                  <strong>{priceLabel(plan, billingCycle, english)}</strong>
                  {!action.reason && <span>/{english ? (billingCycle === 'monthly' ? 'month' : 'year') : (billingCycle === 'monthly' ? '月' : '年')}</span>}
                </div>
                <p className="billing-plan-card__gift">{giftCreditsLabel(plan, billingCycle, english)}</p>
                <p className="billing-plan-card__expiry">{billingExpirationNotice(billingCycle, english)}</p>
                {(plan.details.length > 0 || plan.entitlements.length > 0) && <ul>
                  {(plan.details.length ? plan.details : plan.entitlements.map(item => `${item.name}：${item.value || '—'}`)).map((detail, detailIndex) => (
                    <li key={`${plan.id}-${detailIndex}`}><Check size={13} />{detail}</li>
                  ))}
                </ul>}
                {action.reason && <p className="billing-plan-card__expiry">{action.reason}</p>}
                {!action.hidden && <button
                  type="button"
                  disabled={action.disabled}
                  title={action.reason}
                  onClick={() => !action.disabled && setSelectedCheckout({ plan, billingCycle })}
                >{action.label}</button>}
                {action.hidden && <p className="billing-plan-card__expiry">{english ? 'Only the current team owner or admin can purchase.' : '仅当前团队的所有者或管理员可以购买。'}</p>}
              </article>
            );
          })}
        </section>
      )}

      {(catalogStatus === 'ready' || catalogStatus === 'stale') && plans.length > 0 && matrixRows.length > 0 && (
        <section className="account-settings-card billing-matrix-card">
          <div className="account-settings-card__heading">
            <div>
              <h2>{english ? 'Compare Benefits' : '权益对比'}</h2>
            </div>
          </div>
            <div className="billing-matrix" role="table" aria-label={english ? 'Plan benefit comparison' : '套餐权益对比'}>
              <div
                className="billing-matrix__row billing-matrix__header"
                role="row"
                style={{ gridTemplateColumns: `minmax(150px, 1.3fr) repeat(${plans.length}, minmax(90px, 1fr))` }}
              >
                <span role="columnheader">{english ? 'Benefit' : '权益'}</span>
                {plans.map(plan => <span role="columnheader" key={plan.id}>{plan.name}</span>)}
              </div>
              {matrixRows.map(row => (
                <div
                  className="billing-matrix__row"
                  role="row"
                  key={row.key}
                  style={{ gridTemplateColumns: `minmax(150px, 1.3fr) repeat(${plans.length}, minmax(90px, 1fr))` }}
                >
                  <span role="cell"><strong>{row.name}</strong></span>
                  {plans.map(plan => <span role="cell" key={plan.id}>{row.values[String(plan.id)] || '—'}</span>)}
                </div>
              ))}
            </div>
        </section>
      )}

      {!teamCatalog && <section className="billing-faq">
        <span className="account-eyebrow">FAQ</span>
        <h2>{english ? 'Plan Details' : '套餐说明'}</h2>
        {(english ? [
          ['How can I check prices and benefits?', 'See the current plan details for prices and included benefits.'],
          ['When does an upgrade take effect?', 'A higher plan starts after full payment. Monthly-to-yearly starts a new full year from the successful payment time.'],
          ['How are annual credits issued?', 'Annual credits are issued in 12 monthly grants. The first 11 expire at the next grant; the last expires with the subscription.'],
        ] : [
          ['如何查看套餐价格和权益？', '价格与权益以当前套餐详情为准。'],
          ['升级后什么时候生效？', '高版本套餐全额支付成功后生效；月付改年付从本次支付成功时间起重新计算完整一年。'],
          ['年付积分如何发放？', '年付分 12 期逐月发放，前 11 期未使用点数在下一期发放时过期，第 12 期随订阅到期失效。'],
        ]).map(([question, answer]) => (
          <details key={question}>
            <summary>{question}<ChevronDown size={15} /></summary>
            <p>{brand.text(answer)}</p>
          </details>
        ))}
      </section>}
      {selectedCheckout && <SubscriptionCheckoutDialog
        plan={selectedCheckout.plan}
        billingCycle={selectedCheckout.billingCycle}
        team={teamScope}
        english={english}
        onClose={() => setSelectedCheckout(null)}
      />}
    </>
  );
}
